import { useState, useMemo, useCallback, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress, Divider, Grid, IconButton, InputAdornment, Link, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalculateIcon from '@mui/icons-material/Calculate';
import SummarizeIcon from '@mui/icons-material/Summarize';
import loanClosingCostsApi from '../../../services/loanClosingCostsApi';
import CurrencyInput from '../../../components/shared/CurrencyInput';
import SectionHeader from '../../../components/shared/SectionHeader';
import SharedDataItem from '../../../components/shared/DataItem';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { getAxiosErrorMessage } from '../../../utils/getAxiosErrorMessage';

const formatCurrency = (value) => {
  const num = Number(value) || 0;
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatPercent = (value) => `${Number(value).toFixed(3)}%`;

const determinePayer = (cc) => {
  const borrower = (cc.borrowerPaidAtClose || 0) + (cc.borrowerPaidBeforeClose || 0);
  const lender = (cc.lenderPaidAtClose || 0) + (cc.lenderPaidBeforeClose || 0);
  const broker = (cc.brokerPaidAtClose || 0) + (cc.brokerPaidBeforeClose || 0);
  const seller = (cc.sellerPaidAtClose || 0) + (cc.sellerPaidBeforeClose || 0);
  const other = (cc.otherPaidAtClose || 0) + (cc.otherPaidBeforeClose || 0);
  const sellerTotal = seller + other;

  if (lender > 0 && lender >= borrower && lender >= sellerTotal && lender >= broker) return 'Lender';
  if (sellerTotal > 0 && sellerTotal >= borrower && sellerTotal >= lender && sellerTotal >= broker) return 'Seller';
  if (broker > 0 && broker >= borrower) return 'Broker';
  return 'Borrower';
};

const feeAmount = (cc) => {
  const borrower = (cc.borrowerPaidAtClose || 0) + (cc.borrowerPaidBeforeClose || 0);
  const lender = (cc.lenderPaidAtClose || 0) + (cc.lenderPaidBeforeClose || 0);
  const broker = (cc.brokerPaidAtClose || 0) + (cc.brokerPaidBeforeClose || 0);
  const seller = (cc.sellerPaidAtClose || 0) + (cc.sellerPaidBeforeClose || 0);
  const other = (cc.otherPaidAtClose || 0) + (cc.otherPaidBeforeClose || 0);
  const sum = borrower + lender + broker + seller + other;
  return sum || cc.totalAmount || 0;
};

const PAYER_OPTIONS = [
  { value: 'Borrower', label: 'Borrower', color: 'error' },
  { value: 'Lender', label: 'Lender', color: 'success' },
  { value: 'Seller', label: 'Seller', color: 'warning' },
  { value: 'Broker', label: 'Broker', color: 'secondary' },
];

const CALC_METHOD_OPTIONS = [
  { value: 'FixedAmount', label: 'Fixed $' },
  { value: 'PercentOfLoan', label: '% of Loan' },
];

// Fee type options per section — values match Vesta enums
const CLOSING_FEE_TYPES = [
  { value: 'LoanOriginationFee', label: 'Loan Origination Fee' },
  { value: 'Points', label: 'Points' },
  { value: 'TemporaryBuydownPoints', label: 'Temporary Buydown Points' },
  { value: 'ProcessingFee', label: 'Processing Fee' },
  { value: 'UnderwritingFee', label: 'Underwriting Fee' },
  { value: 'ApplicationFee', label: 'Application Fee' },
  { value: 'MortgageBrokerFee', label: 'Mortgage Broker Fee' },
  { value: 'CommitmentFee', label: 'Commitment Fee' },
  { value: 'RateLockFee', label: 'Rate Lock Fee' },
  { value: 'LockExtensionFee', label: 'Lock Extension Fee' },
  { value: 'AppraisalFee', label: 'Appraisal Fee' },
  { value: 'AppraisalDeskReviewFee', label: 'Appraisal Desk Review' },
  { value: 'AppraisalManagementCompanyFee', label: 'Appraisal Management Fee' },
  { value: 'CreditReportFee', label: 'Credit Report Fee' },
  { value: 'FloodCertification', label: 'Flood Certification' },
  { value: 'FloodCertificationLifeOfLoan', label: 'Flood Cert (Life of Loan)' },
  { value: 'TitleSearch', label: 'Title Search' },
  { value: 'LendersTitleInsurance', label: "Lender's Title Insurance" },
  { value: 'OwnersTitleInsurance', label: "Owner's Title Insurance" },
  { value: 'SettlementOrClosingFee', label: 'Settlement / Closing Fee' },
  { value: 'AttorneyFee', label: 'Attorney Fee' },
  { value: 'LendersAttorneyFee', label: "Lender's Attorney Fee" },
  { value: 'SurveyFee', label: 'Survey Fee' },
  { value: 'PestInspectionFee', label: 'Pest Inspection Fee' },
  { value: 'HomeInspectionFee', label: 'Home Inspection Fee' },
  { value: 'HomeWarrantyFee', label: 'Home Warranty Fee' },
  { value: 'LenderCourierFee', label: 'Courier Fee' },
  { value: 'WireTransferFee', label: 'Wire Transfer Fee' },
  { value: 'LenderTaxServiceFee', label: 'Tax Service Fee' },
  { value: 'LenderDocumentPreparationFee', label: 'Document Preparation Fee' },
  { value: 'BankruptcyMonitoringFee', label: 'Bankruptcy Monitoring Fee' },
  { value: 'SubordinationFee', label: 'Subordination Fee' },
  { value: 'Other', label: 'Other' },
];

const TAX_GOV_FEE_TYPES = [
  { value: 'RecordingFeeForDeed', label: 'Recording Fee — Deed' },
  { value: 'RecordingFeeForMortgage', label: 'Recording Fee — Mortgage' },
  { value: 'RecordingFeeForRelease', label: 'Recording Fee — Release' },
  { value: 'RecordingFeeForAssignment', label: 'Recording Fee — Assignment' },
  { value: 'RecordingFeeForSubordination', label: 'Recording Fee — Subordination' },
  { value: 'RecordingFeeForOtherDocument', label: 'Recording Fee — Other' },
  { value: 'RecordingServiceFee', label: 'Recording Service Fee' },
  { value: 'TransferTaxTotal', label: 'Transfer Tax — Total' },
  { value: 'TaxStampForCityDeed', label: 'Tax Stamp — City Deed' },
  { value: 'TaxStampForCountyDeed', label: 'Tax Stamp — County Deed' },
  { value: 'TaxStampForStateDeed', label: 'Tax Stamp — State Deed' },
  { value: 'TaxStampForCityMortgage', label: 'Tax Stamp — City Mortgage' },
  { value: 'TaxStampForCountyMortgage', label: 'Tax Stamp — County Mortgage' },
  { value: 'TaxStampForStateMortgage', label: 'Tax Stamp — State Mortgage' },
  { value: 'Other', label: 'Other' },
];

const PREPAID_EXPENSE_TYPES = [
  { value: 'HomeownersInsurancePremium', label: "Homeowner's Insurance Premium" },
  { value: 'PropertyTax', label: 'Property Tax' },
  { value: 'PrepaidInterest', label: 'Prepaid Interest' },
  { value: 'FloodInsurancePremium', label: 'Flood Insurance Premium' },
  { value: 'MortgageInsurancePremium', label: 'Mortgage Insurance Premium' },
  { value: 'HomeownersAssociationDues', label: 'HOA Dues' },
  { value: 'USDARuralDevelopmentGuaranteeFee', label: 'USDA Guarantee Fee' },
  { value: 'Other', label: 'Other' },
];

const ESCROW_PAYMENT_TYPES = [
  { value: 'HomeownersInsurancePremium', label: "Homeowner's Insurance" },
  { value: 'PropertyTax', label: 'Property Tax' },
  { value: 'FloodInsurancePremium', label: 'Flood Insurance' },
  { value: 'MortgageInsurancePremium', label: 'Mortgage Insurance' },
  { value: 'USDARuralDevelopmentGuaranteeFee', label: 'USDA Guarantee Fee' },
  { value: 'VAFundingFee', label: 'VA Funding Fee' },
  { value: 'StateTax', label: 'State Tax' },
  { value: 'CityTax', label: 'City Tax' },
  { value: 'Other', label: 'Other' },
];

const FEE_TYPES_BY_SECTION = {
  origination: CLOSING_FEE_TYPES,
  cannotShop: CLOSING_FEE_TYPES,
  canShop: CLOSING_FEE_TYPES,
  taxesGov: TAX_GOV_FEE_TYPES,
  prepaids: PREPAID_EXPENSE_TYPES,
  escrow: ESCROW_PAYMENT_TYPES,
  other: CLOSING_FEE_TYPES,
};

const SECTION_KEYS = [
  { key: 'origination', title: 'A. Origination Charges', isLOSProtected: true },
  { key: 'cannotShop', title: 'B. Services Borrower Cannot Shop For', isLOSProtected: false },
  { key: 'canShop', title: 'C. Services Borrower Can Shop For', isLOSProtected: false },
  { key: '_totalLoanCosts', title: 'D. Total Loan Costs (A + B + C)', isSummary: true },
  { key: 'taxesGov', title: 'E. Taxes and Government Fees', isLOSProtected: false },
  { key: 'prepaids', title: 'F. Prepaids', isLOSProtected: false },
  { key: 'escrow', title: 'G. Initial Escrow Payment at Closing', isLOSProtected: false },
  { key: 'other', title: 'H. Other', isLOSProtected: false },
];

// ── Zod schemas ──

const amountRefinement = (data, ctx) => {
  if (data.calculationMethod === 'PercentOfLoan') {
    if (data.amount == null || data.amount <= 0 || data.amount > 100) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Must be between 0 and 100', path: ['amount'] });
    }
  } else if (data.amount == null || data.amount <= 0) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Must be greater than zero', path: ['amount'] });
  }
};

const addFeeSchema = z.object({
  feeType: z.string().min(1, 'Fee type is required'),
  calculationMethod: z.enum(['FixedAmount', 'PercentOfLoan']),
  amount: z.number({ required_error: 'Required', invalid_type_error: 'Required' }).nullable(),
  payer: z.string().min(1, 'Payer is required'),
}).superRefine(amountRefinement);

const editFeeSchema = z.object({
  calculationMethod: z.enum(['FixedAmount', 'PercentOfLoan']),
  amount: z.number({ required_error: 'Required', invalid_type_error: 'Required' }).nullable(),
  payer: z.string().min(1, 'Payer is required'),
}).superRefine(amountRefinement);

// ── AmountField: renders CurrencyInput (fixed) or % TextField (percent) ──

const AmountField = ({ control, isPercent }) => (
  <Controller
    name="amount"
    control={control}
    render={({ field, fieldState }) =>
      isPercent ? (
        <TextField
          size="small" fullWidth type="number"
          value={field.value ?? ''}
          onChange={(e) => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
          onBlur={field.onBlur}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          inputProps={{ min: 0, max: 100, step: 0.001 }}
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
        />
      ) : (
        <CurrencyInput
          size="small" fullWidth prefix="" placeholder="0.00"
          value={field.value ?? ''}
          onChange={(v) => field.onChange(typeof v === 'number' ? v : null)}
          onBlur={field.onBlur}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
        />
      )
    }
  />
);

// ── AddFeeRow component ──

const AddFeeRow = ({ sectionKey, onAdd, onCancel, isSubmitting }) => {
  const feeTypes = FEE_TYPES_BY_SECTION[sectionKey] || CLOSING_FEE_TYPES;
  const { control, handleSubmit, watch } = useForm({
    resolver: zodResolver(addFeeSchema),
    defaultValues: { feeType: '', calculationMethod: 'FixedAmount', amount: null, payer: 'Borrower' },
  });
  const isPercent = watch('calculationMethod') === 'PercentOfLoan';

  return (
    <TableRow sx={{ bgcolor: 'action.hover' }}>
      <TableCell>
        <Controller
          name="feeType"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              select size="small" fullWidth
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              autoFocus
            >
              <MenuItem value="">Select fee type...</MenuItem>
              {feeTypes.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
          )}
        />
      </TableCell>
      <TableCell sx={{ width: 220 }}>
        <Stack direction="row" spacing={0.5}>
          <Controller
            name="calculationMethod"
            control={control}
            render={({ field }) => (
              <TextField select size="small" sx={{ width: 200 }} value={field.value} onChange={(e) => field.onChange(e.target.value)}>
                {CALC_METHOD_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            )}
          />
          <AmountField control={control} isPercent={isPercent} />
        </Stack>
      </TableCell>
      <TableCell sx={{ width: 200 }}>
        <Controller
          name="payer"
          control={control}
          render={({ field }) => (
            <TextField select size="small" fullWidth value={field.value} onChange={(e) => field.onChange(e.target.value)}>
              {PAYER_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
          )}
        />
      </TableCell>
      <TableCell align="right" sx={{ width: 120 }}>
        <Button
          size="small" variant="contained"
          onClick={handleSubmit(onAdd)}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={14} /> : <CheckIcon />}
        >
          Add
        </Button>
        <IconButton size="small" onClick={onCancel} disabled={isSubmitting}><CloseIcon fontSize="small" /></IconButton>
      </TableCell>
    </TableRow>
  );
};

// ── EditFeeRow component ──

const EditFeeRow = ({ fee, onSave, onCancel, isSubmitting }) => {
  const defaultAmount = fee.calculationMethod === 'PercentOfLoan'
    ? (fee.percentOfLoanAmount ?? null)
    : (fee.amount ?? null);

  const { control, handleSubmit, watch } = useForm({
    resolver: zodResolver(editFeeSchema),
    defaultValues: {
      calculationMethod: fee.calculationMethod || 'FixedAmount',
      amount: defaultAmount,
      payer: fee.payer,
    },
  });
  const isPercent = watch('calculationMethod') === 'PercentOfLoan';

  return (
    <TableRow key={fee.id}>
      <TableCell>
        <Typography variant="body2" color="text.secondary">{fee.name}</Typography>
      </TableCell>
      <TableCell sx={{ width: 220 }}>
        <Stack direction="row" spacing={0.5}>
          <Controller
            name="calculationMethod"
            control={control}
            render={({ field }) => (
              <TextField select size="small" sx={{ width: 200 }} value={field.value} onChange={(e) => field.onChange(e.target.value)}>
                {CALC_METHOD_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            )}
          />
          <AmountField control={control} isPercent={isPercent} />
        </Stack>
      </TableCell>
      <TableCell sx={{ width: 200 }}>
        <Controller
          name="payer"
          control={control}
          render={({ field }) => (
            <TextField select size="small" fullWidth value={field.value} onChange={(e) => field.onChange(e.target.value)}>
              {PAYER_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
          )}
        />
      </TableCell>
      <TableCell align="right" sx={{ width: 120 }}>
        <IconButton
          size="small" color="primary"
          onClick={handleSubmit(onSave)}
          disabled={isSubmitting}
          title="Save"
        >
          {isSubmitting ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
        </IconButton>
        <IconButton size="small" onClick={onCancel} disabled={isSubmitting} title="Cancel">
          <CloseIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

// ── Main component ──

const FeesWorksheetComponent = ({ loanId, loanDetail, onNavigateToPricing, isReadOnly = false }) => {
  const { data: closingCosts, isLoading, error, refetch } = loanClosingCostsApi.endpoints.getLoanClosingCosts.useQuery(loanId, { skip: !loanId });
  const [runFees] = loanClosingCostsApi.endpoints.runFees.useMutation();
  const [saveClosingCostChanges] = loanClosingCostsApi.endpoints.saveClosingCostChanges.useMutation();

  const isOrigination = (loanDetail?.loan?.stage || '').toLowerCase() === 'origination';
  const [savingChanges, setSavingChanges] = useState(false);
  const [addingFee, setAddingFee] = useState(false);

  const [feesRunning, setFeesRunning] = useState(false);
  const [feesError, setFeesError] = useState(null);

  const [editingFeeId, setEditingFeeId] = useState(null);
  const [addingToSection, setAddingToSection] = useState(null);

  const loanAmount = loanDetail?.loan?.loanAmount || 0;
  const propertyValue = loanDetail?.property?.purchasePrice || loanDetail?.property?.appraisedValue || 0;
  const downPayment = propertyValue - loanAmount;

  const [originalSections, setOriginalSections] = useState(null);
  const [localSections, setLocalSections] = useState(null);

  useEffect(() => {
    if (!closingCosts) {
      setOriginalSections(null);
      setLocalSections(null);
      return;
    }

    const mapped = mapSections(closingCosts);
    setOriginalSections(mapped);
    setLocalSections(mapped);
  }, [closingCosts]);

  const sections = localSections;

  const allFees = useMemo(() => {
    if (!sections) return [];
    return Object.values(sections).flat();
  }, [sections]);

  const totalLoanCosts = useMemo(() => {
    if (!sections) return 0;
    return [...(sections['origination'] || []), ...(sections['cannotShop'] || []), ...(sections['canShop'] || [])]
      .reduce((s, f) => s + (f.amount || 0), 0);
  }, [sections]);

  const summaryTotals = useMemo(() => {
    if (!allFees.length) return null;
    return {
      totalBorrowerPaid: allFees.filter(f => f.payer === 'Borrower').reduce((s, f) => s + (f.amount || 0), 0),
      totalLenderPaid: allFees.filter(f => f.payer === 'Lender').reduce((s, f) => s + (f.amount || 0), 0),
      totalSellerPaid: allFees.filter(f => f.payer === 'Seller').reduce((s, f) => s + (f.amount || 0), 0),
      totalBrokerPaid: allFees.filter(f => f.payer === 'Broker').reduce((s, f) => s + (f.amount || 0), 0),
    };
  }, [allFees]);

  const cashToClose = useMemo(() => {
    if (!summaryTotals) return null;
    const closingCostTotal = summaryTotals.totalBorrowerPaid;
    const lenderCredits = summaryTotals.totalLenderPaid;
    const sellerCredits = summaryTotals.totalSellerPaid;
    return {
      downPayment,
      closingCosts: closingCostTotal,
      lenderCredits,
      sellerCredits,
      cashToClose: downPayment + closingCostTotal - lenderCredits - sellerCredits,
    };
  }, [summaryTotals, downPayment]);

  // ── Run Fees ──
  const handleRunFees = useCallback(async () => {
    setFeesRunning(true);
    setFeesError(null);
    try {
      await runFees(loanId).unwrap();
      setEditingFeeId(null);
      setAddingToSection(null);
      refetch();
    } catch (err) {
      setFeesError(getAxiosErrorMessage(err, 'Failed to run fees.'));
    } finally {
      setFeesRunning(false);
    }
  }, [loanId, runFees, refetch]);

  // ── Add Fee ──
  const handleAddFee = useCallback(async (sectionKey, data) => {
    if (isReadOnly) return;
    setAddingFee(true);
    setFeesError(null);
    try {
      const feeTypeLabel = (FEE_TYPES_BY_SECTION[sectionKey] || CLOSING_FEE_TYPES).find((t) => t.value === data.feeType)?.label || data.feeType;
      const computedAmount = data.calculationMethod === 'PercentOfLoan'
        ? (Number(loanAmount) * Number(data.amount || 0)) / 100
        : Number(data.amount || 0);

      const newFee = {
        id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name: feeTypeLabel,
        feeType: data.feeType,
        description: '',
        amount: computedAmount,
        payer: data.payer,
        isManuallyEdited: true,
        calculationMethod: data.calculationMethod,
        percentOfLoanAmount: data.calculationMethod === 'PercentOfLoan' ? Number(data.amount || 0) : null,
      };

      setLocalSections((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          [sectionKey]: [...(prev[sectionKey] || []), newFee],
        };
      });
      setAddingToSection(null);
    } catch (err) {
      setFeesError(getAxiosErrorMessage(err, 'Failed to add fee.'));
    } finally {
      setAddingFee(false);
    }
  }, [isReadOnly, loanAmount]);

  // ── Edit Fee ──
  const handleSaveEdit = useCallback(async (fee, data) => {
    if (isReadOnly) return;
    setFeesError(null);
    try {
      const updatedAmount = data.calculationMethod === 'PercentOfLoan'
        ? (Number(loanAmount) * Number(data.amount || 0)) / 100
        : Number(data.amount || 0);

      setLocalSections((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          next[key] = (next[key] || []).map((sectionFee) => sectionFee.id === fee.id
            ? {
              ...sectionFee,
              calculationMethod: data.calculationMethod,
              amount: updatedAmount,
              payer: data.payer,
              percentOfLoanAmount: data.calculationMethod === 'PercentOfLoan' ? Number(data.amount || 0) : null,
              isManuallyEdited: true,
            }
            : sectionFee);
        }
        return next;
      });
      setEditingFeeId(null);
    } catch (err) {
      setFeesError(getAxiosErrorMessage(err, 'Failed to save fee change.'));
    }
  }, [isReadOnly, loanAmount]);

  // ── Delete Fee ──
  const handleDeleteLosFee = async (fee) => {
    if (isReadOnly) return;
    if (!fee.id) return;
    setFeesError(null);
    setLocalSections((prev) => {
      if (!prev) return prev;
      const next = {};
      for (const [key, fees] of Object.entries(prev)) {
        next[key] = (fees || []).filter((sectionFee) => sectionFee.id !== fee.id);
      }
      return next;
    });
    if (editingFeeId === fee.id) {
      setEditingFeeId(null);
    }
  };

  const pendingChanges = useMemo(() => computeClosingCostChanges(originalSections, localSections), [originalSections, localSections]);
  const hasPendingChanges = pendingChanges.creates.length > 0 || pendingChanges.updates.length > 0 || pendingChanges.deletes.length > 0;

  const handleCancelAllChanges = useCallback(() => {
    if (!originalSections) return;

    setFeesError(null);
    setEditingFeeId(null);
    setAddingToSection(null);
    setAddingFee(false);
    setLocalSections(originalSections);
  }, [originalSections]);

  const handleSaveAllChanges = useCallback(async () => {
    if (!hasPendingChanges) return;

    setSavingChanges(true);
    setFeesError(null);
    setEditingFeeId(null);
    setAddingToSection(null);
    try {
      await saveClosingCostChanges({
        loanId,
        data: pendingChanges,
      }).unwrap();
      await refetch();
    } catch (err) {
      setFeesError(getAxiosErrorMessage(err, 'Failed to save fee changes.'));
    } finally {
      setSavingChanges(false);
    }
  }, [hasPendingChanges, loanId, pendingChanges, refetch, saveClosingCostChanges]);

  // ── Render ──
  const renderFeeRow = (fee, sectionKey) => {
    const isAutoCalculated = fee.feeType === 'MortgageBrokerFee';
    const isEditing = editingFeeId === fee.id;
    const isPercent = fee.calculationMethod === 'PercentOfLoan';

    if (isEditing) {
      return (
        <EditFeeRow
          key={fee.id}
          fee={fee}
          onSave={(data) => handleSaveEdit(fee, data)}
          onCancel={() => setEditingFeeId(null)}
          isSubmitting={savingChanges}
        />
      );
    }

    const payerColor = PAYER_OPTIONS.find(o => o.value === fee.payer)?.color || 'default';

    return (
      <TableRow key={fee.id} hover>
        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box>
              <Typography variant="body2" fontWeight={700}>{fee.name}</Typography>
              {fee.description && <Typography variant="caption" color="text.secondary">{fee.description}</Typography>}
            </Box>
            {fee.isManuallyEdited && <Chip label="Edited" size="small" color="warning" variant="outlined" />}
            {isPercent && <Chip label="% of Loan" size="small" variant="outlined" />}
          </Stack>
        </TableCell>
        <TableCell align="right" sx={{ width: 220 }}>
          <Typography variant="body2" fontWeight={700}>{formatCurrency(fee.amount)}</Typography>
          {isPercent && fee.percentOfLoanAmount != null && (
            <Typography variant="caption" color="text.secondary">{formatPercent(fee.percentOfLoanAmount)}</Typography>
          )}
        </TableCell>
        <TableCell sx={{ width: 200 }}><Chip label={fee.payer} size="small" color={payerColor} variant="outlined" /></TableCell>
        <TableCell align="right" sx={{ width: 120 }}>
          {isAutoCalculated ? (
            <Typography variant="caption" color="text.secondary" fontStyle="italic">Auto-calculated</Typography>
          ) : (
            <>
              {!isReadOnly && (
                <IconButton size="small" onClick={() => setEditingFeeId(fee.id)} title="Edit"><EditIcon fontSize="small" /></IconButton>
              )}
              {!isReadOnly && (
                <IconButton size="small" color="error" onClick={() => handleDeleteLosFee(fee)} disabled={savingChanges} title="Delete fee">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const renderFeeSection = (sectionKey, title, fees, isLOSProtected) => {
    if (!fees) return null;
    const sectionTotal = fees.reduce((sum, fee) => sum + (fee.amount || 0), 0);
    const borrowerTotal = fees.filter(f => f.payer === 'Borrower').reduce((sum, fee) => sum + (fee.amount || 0), 0);
    const isAdding = addingToSection === sectionKey;

    return (
      <Card variant="outlined" sx={{ mb: 2 }} key={sectionKey}>
        <CardHeader
          title={<SectionHeader title={title} icon={<ReceiptLongIcon fontSize="small" />} sx={{ mb: 0 }} />}
          action={
            <Button
              size="small"
              variant="outlined"
              startIcon={isAdding ? <CloseIcon /> : <AddIcon />}
              onClick={() => setAddingToSection(isAdding ? null : sectionKey)}
              disabled={isReadOnly || savingChanges}
            >
              {isAdding ? 'Cancel' : 'Add Fee'}
            </Button>
          }
        />
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Fee Description</TableCell>
              <TableCell align="right" sx={{ width: 220 }}>Amount</TableCell>
              <TableCell sx={{ width: 200 }}>Paid By</TableCell>
              <TableCell align="right" sx={{ width: 120 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fees.length === 0 && !isAdding && sectionKey === 'origination' && !loanDetail?.loan?.programName && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Alert severity="info" sx={{ my: 1 }}>
                    <strong>No origination fees yet.</strong> Fees such as Mortgage Broker Fee, Origination Fee, and Points are auto-calculated when you assign a product through pricing.{' '}
                    {onNavigateToPricing ? (
                      <>Go to the <Link component="button" onClick={onNavigateToPricing}>Pricing tab</Link> to run pricing and lock a rate — Section A fees will populate automatically.</>
                    ) : (
                      <>Run Pricing to assign a product and these fees will populate automatically.</>
                    )}
                  </Alert>
                </TableCell>
              </TableRow>
            )}
            {fees.length === 0 && !isAdding && (sectionKey !== 'origination' || loanDetail?.loan?.programName) && (
              <TableRow><TableCell colSpan={4} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No fees in this section.</Typography></TableCell></TableRow>
            )}
            {fees.map(fee => renderFeeRow(fee, sectionKey))}
            {isAdding && (
              <AddFeeRow
                sectionKey={sectionKey}
                onAdd={(data) => handleAddFee(sectionKey, data)}
                onCancel={() => setAddingToSection(null)}
                isSubmitting={addingFee}
              />
            )}
            {fees.length > 0 && (
              <TableRow sx={{ bgcolor: 'action.selected' }}>
                <TableCell><Typography variant="body2" fontWeight={700}>Subtotal</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight={700}>{formatCurrency(sectionTotal)}</Typography></TableCell>
                <TableCell colSpan={2}><Typography variant="caption" color="text.secondary">Borrower pays: {formatCurrency(borrowerTotal)}</Typography></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    );
  };

  if (isLoading) {
    return <Card variant="outlined"><CardContent sx={{ textAlign: 'center', py: 5 }}><CircularProgress sx={{ mb: 2 }} /><Typography>Loading closing costs...</Typography></CardContent></Card>;
  }

  if (error) {
    return (
      <Card variant="outlined"><CardContent sx={{ textAlign: 'center', py: 5 }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>Failed to load closing costs</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{getAxiosErrorMessage(error, 'An error occurred.')}</Typography>
        <Button variant="contained" startIcon={<RefreshIcon />} onClick={refetch}>Retry</Button>
      </CardContent></Card>
    );
  }

  const DataItem = (props) => <SharedDataItem bold {...props} />;

  return (
    <Box>
      {/* Header */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardHeader
          title={<SectionHeader title="Fees & Loan Estimate" icon={<ReceiptLongIcon fontSize="small" />} sx={{ mb: 0 }} />}
        />
        <CardContent sx={{ pt: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            {!isReadOnly && hasPendingChanges && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveAllChanges}
                disabled={savingChanges || feesRunning}
                startIcon={savingChanges ? <CircularProgress size={16} /> : <CheckIcon />}
              >
                {savingChanges ? 'Saving...' : 'Save'}
              </Button>
            )}
            {!isReadOnly && hasPendingChanges && (
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleCancelAllChanges}
                disabled={savingChanges || feesRunning}
                startIcon={<CloseIcon />}
              >
                Cancel
              </Button>
            )}
            {!isReadOnly && isOrigination && !hasPendingChanges && (
              <Button
                variant="contained"
                onClick={handleRunFees}
                disabled={feesRunning || savingChanges || hasPendingChanges}
                startIcon={feesRunning ? <CircularProgress size={16} /> : <CalculateIcon />}
              >
                {feesRunning ? 'Running Fees...' : 'Run Fees'}
              </Button>
            )}
            {isReadOnly && (
              <Alert severity="info" sx={{ py: 0 }}>
                This loan is read-only. Fee edits and running fees are disabled.
              </Alert>
            )}
          </Stack>
          {feesError && <Alert severity="error" sx={{ mt: 1 }}>{feesError}</Alert>}
          {feesRunning && <Alert severity="info" sx={{ mt: 1 }}>The LOS is pulling and applying fees to this loan. This may take a moment...</Alert>}
          {!feesRunning && !feesError && !sections && <Alert severity="warning" sx={{ mt: isOrigination ? 1 : 0 }}>No fees loaded yet.{(!isReadOnly && isOrigination) ? <> Click <strong>Run Fees</strong> to pull fees from the LOS.</> : ' Fees can only be run during editable Origination stage loans.'}</Alert>}
        </CardContent>
      </Card>

      {sections && (
        <Grid container spacing={2}>
          {/* Left Column: Fee Sections */}
          <Grid size={{ xs: 12, lg: 8 }}>
            {SECTION_KEYS.map(({ key, title, isLOSProtected, isSummary }) =>
              isSummary ? (
                <Card variant="outlined" sx={{ mb: 2, bgcolor: 'rgba(var(--mui-palette-info-mainChannel) / 0.10)' }} key={key}>
                  <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
                    <Typography variant="subtitle2">{title}</Typography>
                    <Typography variant="h5" fontWeight={700}>{formatCurrency(totalLoanCosts)}</Typography>
                  </CardContent>
                </Card>
              ) : renderFeeSection(key, title, sections[key], isLOSProtected)
            )}
          </Grid>

          {/* Right Column: Fees Snapshot */}
          <Grid size={{ xs: 12, lg: 4 }} sx={{ display: { xs: 'none', lg: 'block' } }}>
            <Box sx={{ position: 'sticky', top: 16 }}>
              <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
                <CardContent sx={{ pb: '12px !important' }}>
                  <SectionHeader title="Fees Snapshot" icon={<SummarizeIcon fontSize="small" />} sx={{ mb: 1 }} />

                  {cashToClose && (
                    <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">Cash to Close</Typography>
                      <Typography variant="h6" fontWeight={700} color="primary.main">{formatCurrency(cashToClose.cashToClose)}</Typography>
                      <Box sx={{ pl: 0.5, mt: 0.5 }}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                          <Typography variant="caption" color="text.secondary">Down Payment</Typography>
                          <Typography variant="caption" fontWeight={600}>{formatCurrency(cashToClose.downPayment)}</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                          <Typography variant="caption" color="text.secondary">+ Closing Costs</Typography>
                          <Typography variant="caption" fontWeight={600}>{formatCurrency(cashToClose.closingCosts)}</Typography>
                        </Stack>
                        {cashToClose.lenderCredits > 0 && (
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                            <Typography variant="caption" color="success.main">− Lender Credits</Typography>
                            <Typography variant="caption" fontWeight={600} color="success.main">({formatCurrency(cashToClose.lenderCredits)})</Typography>
                          </Stack>
                        )}
                        {cashToClose.sellerCredits > 0 && (
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                            <Typography variant="caption" color="success.main">− Seller Credits</Typography>
                            <Typography variant="caption" fontWeight={600} color="success.main">({formatCurrency(cashToClose.sellerCredits)})</Typography>
                          </Stack>
                        )}
                      </Box>
                    </Box>
                  )}

                  <Box sx={{ pl: 0.5 }}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                      <Typography variant="caption" color="text.secondary">Loan Amount</Typography>
                      <Typography variant="caption" fontWeight={700}>{formatCurrency(loanAmount)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                      <Typography variant="caption" color="text.secondary">Property Value</Typography>
                      <Typography variant="caption" fontWeight={600}>{formatCurrency(propertyValue)}</Typography>
                    </Stack>
                    {downPayment > 0 && (
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                        <Typography variant="caption" color="text.secondary">Down Payment</Typography>
                        <Typography variant="caption" fontWeight={600}>{formatCurrency(downPayment)}</Typography>
                      </Stack>
                    )}
                  </Box>

                  {summaryTotals && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ pl: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>By Payer</Typography>
                        {summaryTotals.totalBorrowerPaid > 0 && (
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                            <Typography variant="caption" color="text.secondary">Borrower</Typography>
                            <Typography variant="caption" fontWeight={600}>{formatCurrency(summaryTotals.totalBorrowerPaid)}</Typography>
                          </Stack>
                        )}
                        {summaryTotals.totalLenderPaid > 0 && (
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                            <Typography variant="caption" color="success.main">Lender Credit</Typography>
                            <Typography variant="caption" fontWeight={600} color="success.main">({formatCurrency(summaryTotals.totalLenderPaid)})</Typography>
                          </Stack>
                        )}
                        {summaryTotals.totalSellerPaid > 0 && (
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                            <Typography variant="caption" color="warning.main">Seller Credit</Typography>
                            <Typography variant="caption" fontWeight={600}>{formatCurrency(summaryTotals.totalSellerPaid)}</Typography>
                          </Stack>
                        )}
                        {summaryTotals.totalBrokerPaid > 0 && (
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                            <Typography variant="caption" color="text.secondary">Broker</Typography>
                            <Typography variant="caption" fontWeight={600}>{formatCurrency(summaryTotals.totalBrokerPaid)}</Typography>
                          </Stack>
                        )}
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

const humanize = (s) => s ? s.replace(/([A-Z])/g, ' $1').trim() : null;

function mapFee(cc) {
  const name = humanize(cc.feeType)
    || humanize(cc.prepaidExpenseType)
    || humanize(cc.initialEscrowPaymentType)
    || cc.description
    || 'Unknown Fee';
  return {
    id: cc.closingCostId,
    name,
    feeType: cc.feeType || null,
    description: cc.description || '',
    amount: feeAmount(cc),
    payer: determinePayer(cc),
    isManuallyEdited: cc.isManuallyEdited || false,
    calculationMethod: cc.calculationMethod || 'FixedAmount',
    percentOfLoanAmount: cc.percentOfLoanAmount ?? null,
  };
}

function mapSections(closingCosts) {
  return {
    origination: (closingCosts.origination || []).map(mapFee),
    cannotShop: (closingCosts.cannotShop || []).map(mapFee),
    canShop: (closingCosts.canShop || []).map(mapFee),
    taxesGov: (closingCosts.taxesGov || []).map(mapFee),
    prepaids: (closingCosts.prepaids || []).map(mapFee),
    escrow: (closingCosts.escrow || []).map(mapFee),
    other: (closingCosts.other || []).map(mapFee),
  };
}

function computeClosingCostChanges(originalSections, localSections) {
  if (!originalSections || !localSections) {
    return { creates: [], updates: [], deletes: [] };
  }

  const originalById = new Map();
  const localById = new Map();
  const creates = [];

  for (const [sectionKey, fees] of Object.entries(originalSections)) {
    for (const fee of fees || []) {
      originalById.set(fee.id, { ...fee, sectionKey });
    }
  }

  for (const [sectionKey, fees] of Object.entries(localSections)) {
    for (const fee of fees || []) {
      if (!fee.id || String(fee.id).startsWith('temp-')) {
        creates.push({
          sectionKey,
          feeType: fee.feeType,
          calculationMethod: fee.calculationMethod,
          amount: fee.calculationMethod === 'PercentOfLoan'
            ? Number(fee.percentOfLoanAmount || 0)
            : Number(fee.amount || 0),
          payer: fee.payer,
        });
        continue;
      }

      localById.set(fee.id, { ...fee, sectionKey });
    }
  }

  const updates = [];
  for (const [id, localFee] of localById.entries()) {
    const originalFee = originalById.get(id);
    if (!originalFee) continue;

    const originalAmount = originalFee.calculationMethod === 'PercentOfLoan'
      ? Number(originalFee.percentOfLoanAmount || 0)
      : Number(originalFee.amount || 0);
    const localAmount = localFee.calculationMethod === 'PercentOfLoan'
      ? Number(localFee.percentOfLoanAmount || 0)
      : Number(localFee.amount || 0);

    const changed =
      originalFee.calculationMethod !== localFee.calculationMethod ||
      originalFee.payer !== localFee.payer ||
      originalAmount !== localAmount;

    if (changed) {
      updates.push({
        closingCostId: id,
        calculationMethod: localFee.calculationMethod,
        amount: localAmount,
        payer: localFee.payer,
      });
    }
  }

  const deletes = [];
  for (const id of originalById.keys()) {
    if (!localById.has(id)) {
      deletes.push(id);
    }
  }

  return { creates, updates, deletes };
}

export default FeesWorksheetComponent;
